import {
  ensureSessionCapability,
  SESSION_HEADER,
} from '../core/session/session-identity.js'
import { acceptSessionProjectionEnvelope } from './accept-session-projection-response.js'

/**
 * Store a line the Live socket did not take (workshop intake, a media notice).
 * This is not persistMessageToServer. That callback only re-reads the projection.
 * A busy turn is retried. It is not cancelled.
 */
export async function appendCanonicalExchange(
  sessionId: string,
  userText: string,
  assistantText: string,
): Promise<void> {
  const id = sessionId.trim()
  const visible = userText.trim()
  if (!id || !visible) return
  await ensureSessionCapability(id)
  const inputEventId = globalThis.crypto.randomUUID()

  for (let attempt = 0; attempt < 8; attempt += 1) {
    const response = await fetch('/api/session', {
      method: 'PATCH',
      credentials: 'same-origin',
      cache: 'no-store',
      headers: {
        'Content-Type': 'application/json',
        [SESSION_HEADER]: id,
      },
      body: JSON.stringify({
        sessionId: id,
        exchange: {
          userText: visible,
          assistantText: assistantText.trim(),
          inputEventId,
        },
      }),
    })
    if ((response.status === 409 || response.status === 423) && attempt < 7) {
      await new Promise(resolve => setTimeout(resolve, 400))
      continue
    }
    const payload: unknown = await response.json().catch(() => null)
    if (!response.ok) {
      const message =
        payload && typeof payload === 'object' && typeof (payload as { error?: unknown }).error === 'string'
          ? (payload as { error: string }).error
          : `Canonical exchange was not stored (${response.status})`
      throw new Error(message)
    }
    acceptSessionProjectionEnvelope(payload, '/api/session:exchange')
    return
  }
}

/**
 * Store the typed exchange, then re-read the projection. Never both at once.
 * On 16.10 the refresh ran in parallel with the store. It read the old
 * transcript, dropped the optimistic lines, and the next read added the
 * stored copies: lines vanished, came back in the wrong order, and the close
 * line showed twice.
 */
export async function storeExchangeThenRefresh(
  store: () => Promise<void>,
  refresh: () => Promise<void>,
): Promise<void> {
  try {
    await store()
  } finally {
    await refresh().catch(() => undefined)
  }
}
